import type { StoreOption } from '@/features/campaigns/api/use-campaigns';

/**
 * Lista de lojas da campanha de fidelidade.
 *
 * A collection `stores` já traz `storeUF`. Enquanto não existir um
 * agrupamento oficial (regional, franquia), o estado é o grupo. Trocar
 * `CAMPAIGN_STORE_GROUP_FIELD` quando esse campo chegar — a tela lê daqui.
 */
export const CAMPAIGN_STORE_GROUP_FIELD = 'storeUF' as const;

const UNGROUPED_KEY = '__all__';
const MISSING_GROUP_KEY = '__none__';

const UF_LABEL: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
};

export interface CampaignStoreRow {
  code: string;
  name: string;
  city: string;
  /** Valor cru do campo de agrupamento, já normalizado. Vazio = sem grupo. */
  group: string;
  search: string;
}

export interface CampaignStoreGroup {
  key: string;
  label: string;
  stores: CampaignStoreRow[];
}

export function campaignStoreCode(store: StoreOption): string {
  const raw = store.storeCod ?? store._id ?? '';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'string') return raw.trim();
  return '';
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function fold(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function groupValue(store: StoreOption): string {
  return text(store[CAMPAIGN_STORE_GROUP_FIELD]).toUpperCase();
}

export function groupLabel(key: string): string {
  if (key === MISSING_GROUP_KEY) return 'Sem estado';
  const name = UF_LABEL[key];
  return name ? `${key} · ${name}` : key;
}

export function toCampaignStoreRows(stores: StoreOption[]): CampaignStoreRow[] {
  const rows: CampaignStoreRow[] = [];
  const seen = new Set<string>();
  for (const store of stores) {
    const code = campaignStoreCode(store);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const name = text(store.storeName) || text(store.name) || code;
    const city = text(store.storeCity);
    const group = groupValue(store);
    rows.push({
      code,
      name,
      city,
      group,
      search: fold([name, code, city, group].filter(Boolean).join(' ')),
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  return rows;
}

export function groupCampaignStores(stores: StoreOption[]): {
  grouped: boolean;
  groups: CampaignStoreGroup[];
} {
  const rows = toCampaignStoreRows(stores);
  const grouped = rows.some((row) => row.group !== '');
  if (!grouped) {
    return {
      grouped: false,
      groups: rows.length ? [{ key: UNGROUPED_KEY, label: '', stores: rows }] : [],
    };
  }

  const byKey = new Map<string, CampaignStoreRow[]>();
  for (const row of rows) {
    const key = row.group || MISSING_GROUP_KEY;
    const list = byKey.get(key);
    if (list) list.push(row);
    else byKey.set(key, [row]);
  }

  const groups = [...byKey.entries()]
    .map(([key, list]) => ({ key, label: groupLabel(key), stores: list }))
    .sort((a, b) => {
      if (a.key === MISSING_GROUP_KEY) return 1;
      if (b.key === MISSING_GROUP_KEY) return -1;
      return a.label.localeCompare(b.label, 'pt-BR');
    });

  return { grouped: true, groups };
}

export function filterStoreGroups(groups: CampaignStoreGroup[], query: string): CampaignStoreGroup[] {
  const needle = fold(query.trim());
  if (!needle) return groups;
  return groups
    .map((group) => ({
      ...group,
      stores: group.stores.filter((store) => store.search.includes(needle)),
    }))
    .filter((group) => group.stores.length > 0);
}

/** Marca ou desmarca um conjunto de códigos sem mexer no resto da seleção. */
export function setStoreCodes(selected: string[], codes: string[], on: boolean): string[] {
  if (on) {
    const next = new Set(selected);
    for (const code of codes) next.add(code);
    return [...next];
  }
  const drop = new Set(codes);
  return selected.filter((code) => !drop.has(code));
}
